import axios, { AxiosResponse } from "axios";

// ⛔ MUST be REACT_APP_-prefixed. CRA (react-scripts, no craco/DefinePlugin) inlines ONLY
// `REACT_APP_*` into the browser bundle, so the previous `process.env.API_BASE_URL` was ALWAYS
// undefined here and the fallback below always won — regardless of what the dyno had set.
// Measured 2026-08-14: on relay-assistant-v2 (every API var pointed at botapi-v2) the guest mint
// still went to robot-api. That is the redirect loop: the guest token was minted on the LEGACY
// stack and then used against v2, so /ibot-guest never saw a usable token, waited 6s, and bounced
// back to /signin forever. Same lesson as the Wave-1 REACT_APP_ build-time rule (07-25), which was
// fixed in one file; the prefix guard in relay-server/scripts/check-env-prefix.mjs is what makes
// it stay fixed. ⛔ PATH CORRECTED 2026-08-18: this said `scripts/check-env-prefix.mjs`, which is
// GITIGNORED (.gitignore:55) and therefore absent on a deploy — the guard was moved to
// relay-server/scripts/ at 34b5b90 for exactly that reason. A citation pointing into a gitignored
// directory reads as if the guard exists when nothing would run it.
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  'https://robot-api-37f87b97b291.herokuapp.com';


interface GuestSignInResponse {
  org: {
    orgId?: string;
    [key: string]: any;
  };
  token: string;
  user: {
    id: string;
    orgId: string;
    email: string;
    name: string;
    role: string[];
  };
}

import { getBotSettings } from './botFactoryAPI';
import {
  resolveGuestOrgWithRetry,
  formatGuestOrgLog,
  commitGuestSession,
  commitRefreshedToken,
  jwtSecondsRemaining,
  GUEST_REFRESH_SKEW_SEC,
} from './guestGate';

// Guest Sign In
//
// [S2-A 2026-09-18] `botId` is the bot the mint is FOR. The org is resolved from that bot's
// BotSettings record (GET /botsettings/{id} with the fresh guest token — the home the relay reads
// at bot-config.js:661) and written to `data.org.orgId` + `localStorage.orgId`. Before this the
// value was ONE hardcoded constant — fictional, GB-confirmed — stamped on every guest session of
// every bot, so every guest row in chatHistory / BotMetricsClient carried it. Main still does.
// See porting/design-notes/S2_IDENTITY_DESIGN.md §2 and LEGACY_DIVERGENCES `guest-org-from-record`.
//
// ⛔ The write sites below are LOAD-BEARING: the relay's persistence gate (relayAPI.js:4798-4807)
// skips the chatHistory POST when orgId is falsy. The fix supplies the value; it never deletes
// the write. When no plausible org exists (data) or botapi cannot be reached (infra, retried
// once) the session runs UNATTRIBUTED — orgId '' — loudly (`[GuestOrg]` line), never a constant.
// ⚠️ WRITE ORDER: token/userId are written LAST, after the org resolution has settled, because
// IndexPageGuest's 6 s poll opens the socket the moment it sees them (Grok Note 4).
export async function signInGuest(prompt: string, botId?: string | null): Promise<GuestSignInResponse> {
  try {
    const response: AxiosResponse<GuestSignInResponse> = await axios.post(
      `${API_BASE_URL}/auth/guest`,
      {
        client: {
          userAgent: window.navigator.userAgent || 'unknown',
        },
        network: {
          ip: 'unknown', // This will be overridden by the server's IP detection
        },
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }
    );
    const data = response.data;

    // /auth/guest returns {token, user} — no org (guest.controller.ts:241-244). The org comes
    // from the bot's BotSettings record. Fetch failure is caught inside (never thrown: a botapi
    // blip must not fail the mint — SignInPageGuest would show "Failed to sign in as guest").
    const org = await resolveGuestOrgWithRetry(getBotSettings, botId ?? '', data.token);
    if (org.source !== 'RECORD') {
      console.warn(formatGuestOrgLog(botId, org)); // console, never logRelay (a no-op)
    }

    const orgs: Record<
      string,
      { name: string; logo: string; homepage: string }
    > = {
      'admin-page': {
        name: 'Guest SignIn',
        logo: '/assets/app/favicon.png',
        homepage: '/',
      },
      cmw: { name: 'CMW', logo: '/assets/app/favicon.png', homepage: '/' },
      'river-islands': {
        name: 'Centerbeam.AI',
        logo: '/org/cmw/centerbeam-ai.logo.png',
        homepage: 'https://www.centerbeam.ai',
      },
    };
    localStorage.setItem('orgs', JSON.stringify(orgs));

    // Writes guestMode, tokenTimestamp (FIX 2, 2026-08-14 — the freshness checks read it), orgId,
    // orgSource, orgBotId, user, name — and token + userId LAST. `data.org.orgId` is set here.
    const stored = commitGuestSession(localStorage, data, org, botId ?? '');
    data.org = stored.org;

    // If prompt is provided, store it in localStorage to be used after redirect
    if (prompt) {
      localStorage.setItem('pendingPrompt', prompt);
    }

    return data;
  } catch (error: any) {
    console.error(
      '❌ Guest Sign In Error:',
      error.response?.data ? JSON.stringify(error.response.data) : error.message
    );
    throw error;
  }
}

/**
 * [S2-A Note 8] Re-resolve the org for the page's CURRENT bot. Mint-time org is not
 * session-lifetime org: a `?id=` change or a Header bot switch lands on a different bot without
 * a re-mint. Call before the socket metadata is built. Never throws.
 */
export async function reResolveGuestOrg(botId: string | null | undefined): Promise<void> {
  try {
    if (localStorage.getItem('guestMode') !== 'true') return;
    const token = localStorage.getItem('token');
    if (!token || !botId) return;
    if (localStorage.getItem('orgBotId') === botId && localStorage.getItem('orgId')) return;
    const org = await resolveGuestOrgWithRetry(getBotSettings, botId, token);
    localStorage.setItem('orgId', org.orgId || '');
    localStorage.setItem('orgSource', org.source);
    localStorage.setItem('orgBotId', botId);
    try {
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      stored.org = { ...(stored.org || {}), orgId: org.orgId || '', source: org.source, reason: org.reason || '' };
      localStorage.setItem('user', JSON.stringify(stored));
    } catch { /* best effort */ }
    if (org.source !== 'RECORD') console.warn(formatGuestOrgLog(botId, org));
  } catch (err: any) {
    console.warn('[GuestOrg] re-resolve failed:', err?.message || err);
  }
}
// Decode a base64url segment (JWT parts) tolerantly (handles missing padding).
function base64UrlDecode(segment: string): string {
  const pad = segment.length % 4 === 0 ? '' : '='.repeat(4 - (segment.length % 4));
  const base64 = (segment + pad).replace(/-/g, '+').replace(/_/g, '/');
  return atob(base64);
}

/**
 * Returns true if a guest JWT is missing, unparseable, or expired (with a small
 * clock-skew margin). Used to detect the stale-token case where a returning guest
 * reuses a localStorage token that has passed its `exp` — authed calls that enforce
 * expiry (notably bot metrics) silently 401 in that state.
 */
export function isGuestTokenExpired(jwt: string | null | undefined, skewSec = 60): boolean {
  if (!jwt) return true;
  try {
    const part = jwt.split('.')[1];
    if (!part) return true;
    const { exp } = JSON.parse(base64UrlDecode(part));
    if (!exp) return true;
    return exp * 1000 <= Date.now() + skewSec * 1000;
  } catch {
    return true; // unparseable → treat as bad so we re-mint
  }
}

/**
 * [S2-A Note 5] Silent guest token refresh: POST /auth/guest/refresh with the CURRENT Bearer
 * (guest.controller.ts:248-296 — jwt-authenticated, guest-role gated, issues a new token for the
 * SAME user). Keeps userId, keeps orgId, writes token + tokenTimestamp. Replaces the old
 * `remintGuestToken`, which was a fresh POST /auth/guest: a NEW guest_* identity mid-session,
 * splitting chatHistory/metrics across two users and burning login rate-limit quota.
 * Falls back to a fresh mint ONLY when the refresh itself 401s (the old token is dead), and says
 * so — the identity changes in that one case. Returns the usable token, or null.
 */
export async function refreshGuestToken(): Promise<string | null> {
  const current = localStorage.getItem('token');
  if (!current) return null;
  try {
    const response = await axios.post<{ token: string }>(
      `${API_BASE_URL}/auth/guest/refresh`,
      {},
      { headers: { Authorization: `Bearer ${current}`, Accept: 'application/json' } }
    );
    const token = response.data?.token;
    if (!token) return null;
    commitRefreshedToken(localStorage, token);
    return token;
  } catch (err: any) {
    const status = err?.response?.status;
    if (status !== 401) {
      console.warn('[guest] refresh failed:', err?.response?.data || err?.message || err);
      return null;
    }
  }
  // Refresh rejected the old token outright → a fresh identity is the only way forward.
  try {
    const previousUserId = localStorage.getItem('userId');
    const botId = localStorage.getItem('orgBotId') || localStorage.getItem('lastBotId') || localStorage.getItem('selectedBot');
    const data = await signInGuest('', botId);
    console.warn(`[GuestToken] refresh 401 — re-minted a NEW guest identity (userId ${previousUserId ?? '∅'} → ${data.user?.id ?? '∅'}); history before this point belongs to the old user`);
    return data.token || null;
  } catch (err: any) {
    console.warn('[guest] re-mint after refresh 401 failed:', err?.message || err);
    return null;
  }
}

/** True when the guest JWT is inside the refresh window (exp − now < skew) but not yet expired. */
export function isGuestTokenNearExpiry(jwt: string | null | undefined, skewSec: number = GUEST_REFRESH_SKEW_SEC): boolean {
  const remaining = jwtSecondsRemaining(jwt);
  return remaining !== null && remaining > 0 && remaining < skewSec;
}

// [S2-A] Repointed /ibot → /ibot-guest. No call site today (SignInPageGuest builds its own URL);
// the old target sent a guest to the AUTHED page, where IndexPage.tsx sets guestMode='false'.
export function redirectAfterGuestSignIn(botId: string, prompt?: string) {
  const url = new URL(window.location.origin);
  url.pathname = '/ibot-guest';
  url.searchParams.set('id', botId);
  
  // Use the prompt from localStorage if not provided directly
  const pendingPrompt = prompt || localStorage.getItem('pendingPrompt');
  if (pendingPrompt) {
    url.searchParams.set('prompt', encodeURIComponent(pendingPrompt));
    localStorage.removeItem('pendingPrompt'); // Clean up
  }
  
  window.location.assign(url.toString());
  console.log('✅ Redirecting guest to:', url.toString());
}